import React, {useState, useEffect, useCallback} from 'react';
import { Form, Button, Card, Alert, Tabs, Tab } from 'react-bootstrap';
import { FileText, Cpu, Database, Book, X, MessageSquare, Code, Copy, RefreshCw } from 'lucide-react';
import KnowledgeBaseSelectionModal from '../modals/rag/KnowledgeBaseSelectionModal';
import { API_ENDPOINTS } from '../../config/api.config';
import { apiService } from '../../services/apiService';
import type { KnowledgeBase, RAGFormProps } from '../../types/knowledge-base';
import type {
    IntegrationConfig,
    APIIntegrationConfig,
    IntegrationType,
    MessagingIntegrationConfig
} from '../../types/integrations';
import { RAGIntegrations } from './integrations/RAGIntegrations';
import DeleteConfirmationModal from '../../components/modals/common/DeleteConfirmationModal';

const llmOptions = [
    { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
    { value: 'gpt-4', label: 'GPT-4' },
    { value: 'claude-3-opus', label: 'Claude 3 Opus' },
    { value: 'claude-3-sonnet', label: 'Claude 3 Sonnet' },
    { value: 'mistral-7b', label: 'Mistral 7B' }
];


const renderSectionHeader = (icon: React.ReactNode, title: React.JSX.Element, subtitle?: string) => (

    <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
            <div className="rounded-circle p-2 bg-secondary bg-opacity-10 me-2">
                {icon}
            </div>
            <h6 className="mb-0 fw-medium">{title}</h6>
        </div>
        {subtitle && <small className="text-muted">{subtitle}</small>}
    </div>
);

const RAGForm = React.memo<RAGFormProps>(({
                                              formData,
                                              mode,
                                              onChange,
                                              onSubmit,
                                              onDelete,
                                              error,
                                              isLoading
                                          }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [showKnowledgeBaseModal, setShowKnowledgeBaseModal] = useState(false);
    const [apiTokenCopied, setApiTokenCopied] = useState(false);
    const [curlCopied, setCurlCopied] = useState(false);

    console.log('RAGForm rendering', { mode, formData });

    useEffect(() => {
        // Only fetch if we have IDs but no knowledge bases
        if (formData.indexIds?.length && !formData.selectedKnowledgeBases?.length) {
            fetchSelectedKnowledgeBases();
        }
    }, [formData.indexIds, formData.selectedKnowledgeBases?.length]);

    const fetchSelectedKnowledgeBases = useCallback(async () => {
        if (!formData.indexIds.length) return;

        try {
            const data = await apiService.get(`${API_ENDPOINTS.INDEXES.LIST}?all=true&ids=${formData.indexIds.join(',')}`);
            if (Array.isArray(data)) {
                const selectedKBs = data.filter(kb => formData.indexIds.includes(kb.id));
                onChange('selectedKnowledgeBases', selectedKBs);
            }
        } catch (error) {
            console.error('Error fetching selected knowledge bases:', error);
            setSubmitError('Failed to fetch knowledge base details');
        }
    }, [formData.indexIds, onChange]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);

        if (formData.indexIds.length === 0) {
            setSubmitError('Please select at least one knowledge-base');
            return;
        }

        onSubmit(e);
    };

    const handleRemoveKnowledgeBase = (id: string) => {
        const newIndexIds = formData.indexIds.filter(indexId => indexId !== id);
        const newSelectedKBs = formData.selectedKnowledgeBases.filter(kb => kb.id !== id);
        onChange('indexIds', newIndexIds);
        onChange('selectedKnowledgeBases', newSelectedKBs);
    };

    const handleKnowledgeBaseSelection = (selectedIds: string[], selectedKBs: KnowledgeBase[]): void => {
        onChange('indexIds', selectedIds);
        onChange('selectedKnowledgeBases', selectedKBs);
    };

    const getIntegrationsOfType = <T extends IntegrationType>(
        type: T
    ): Extract<IntegrationConfig, { type: T }>[] => {
        return (formData.integrations?.filter(i => i.type === type) ?? []) as Extract<IntegrationConfig, { type: T }>[];
    };

    const updateIntegrations = <T extends IntegrationType>(
        type: T,
        updated: Extract<IntegrationConfig, { type: T }>[]
    ) => {
        const otherIntegrations = formData.integrations?.filter(i => i.type !== type) ?? [];
        onChange('integrations', [...otherIntegrations, ...updated]);
    };

    const handleUpdateMessagingIntegrations = (updated: MessagingIntegrationConfig[]) => {
        const otherIntegrations = formData.integrations?.filter(i => i.type !== 'messaging') ?? [];
        onChange('integrations', [...otherIntegrations, ...updated]);
    };

    const generateApiToken = () => {
        const token = 'rag_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
        const apiIntegrations = getIntegrationsOfType('api');

        if (apiIntegrations.length > 0) {
            const currentIntegration = apiIntegrations[0];
            updateIntegrations('api', [{
                type: 'api',
                enabled: true,
                settings: {
                    token,
                    rateLimit: currentIntegration.settings.rateLimit ?? 100,
                    allowedOrigins: currentIntegration.settings.allowedOrigins ?? ['*']
                }
            } satisfies APIIntegrationConfig]);
        } else {
            updateIntegrations('api', [{
                type: 'api',
                enabled: true,
                settings: {
                    token,
                    rateLimit: 100,
                    allowedOrigins: ['*']
                }
            } satisfies APIIntegrationConfig]);
        }
    };

    const copyToClipboard = async (text: string, setCopied: (copied: boolean) => void) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const getApiToken = () => {
        const apiIntegrations = getIntegrationsOfType('api');
        if (apiIntegrations.length === 0) return '';
        return apiIntegrations[0].settings.token;
    };

    const getCurlExample = () => {
        const token = getApiToken();
        return `curl -X POST https://api.example.com/v1/rag/${formData.id}/chat \\
  -H "Authorization: Bearer ${token}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "messages": [
      {
        "role": "user",
        "content": "What is the pricing for enterprise plans?"
      }
    ]
  }'`;
    };

    const selectedKBs = formData.selectedKnowledgeBases || [];

    const renderBasicConfigTab = () => (

        <>
            {/* Application Information */}
            <div className="mb-3">
                {renderSectionHeader(
                    <FileText size={16} className="text-secondary" />,
                    <>Application Information <span className="text-danger">*</span></>
                )}
                <Form.Group className="mb-2">
                    <Form.Label className="fw-medium small mb-1">
                        <span className="text-danger me-1">*</span>
                        Name
                    </Form.Label>
                    <Form.Control
                        type="text"
                        value={formData.name ?? ''}
                        onChange={(e) => onChange('name', e.target.value)}
                        required
                        placeholder="Enter application name"
                        className="border"
                        style={{ height: '38px' }}
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label className="fw-medium small mb-1">Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        value={formData.description ?? ''}
                        onChange={(e) => onChange('description', e.target.value)}
                        placeholder="Enter application description"
                        className="border"
                    />
                </Form.Group>
            </div>

            {/* Model Selection */}
            <div className="mb-3">
                {renderSectionHeader(
                    <Cpu size={16} className="text-secondary" />,
                    <>Model Selection <span className="text-danger">*</span></>
                )}
                <Form.Group>
                    <Form.Label className="fw-medium small mb-1">
                        <span className="text-danger me-1">*</span>
                        Language Model (LLM)
                    </Form.Label>
                    <Form.Select
                        value={formData.llmModel ?? ''}
                        onChange={(e) => onChange('llmModel', e.target.value)}
                        required
                        className="border"
                        style={{ height: '38px', paddingTop: '0.375rem', paddingBottom: '0.375rem' }}
                    >
                        <option value="">Select language model</option>
                        {llmOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </div>

            {/* Knowledge Base Selection */}
            <div className="mb-3">
                {renderSectionHeader(
                    <Database size={16} className="text-secondary"/>,
                    <>Knowledge Base Selection <span className="text-danger">*</span></>,
                    formData.indexIds?.length ? `${formData.indexIds.length} Knowledge Base${formData.indexIds.length > 1 ? 's' : ''} selected` : undefined
                )}

                <div className="d-flex align-items-center gap-2 mb-3">
                    <Button
                        variant="outline-primary"
                        style={{height: '38px'}}
                        onClick={() => setShowKnowledgeBaseModal(true)}
                    >
                        Select Knowledge Bases
                    </Button>
                </div>

                {formData.indexIds?.length > 0 && (
                    <div className="border rounded">
                        <div className="p-2">
                            {selectedKBs.map(kb => (
                                <div
                                    key={kb.id}
                                    className="d-flex align-items-center justify-content-between py-1"
                                >
                                    <div className="d-flex align-items-center">
                                        <Book size={16} className="text-primary me-2"/>
                                        <span>{kb.name}</span>
                                    </div>
                                    <Button
                                        variant="link"
                                        className="p-0 ms-2 flex-shrink-0"
                                        onClick={() => handleRemoveKnowledgeBase(kb.id)}
                                        title="Remove from selection"
                                    >
                                        <X size={16}/>
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    const renderIntegrationsTab = () => (
        <div>
            {/* Message App Integrations */}
            <div className="mb-4">
                {renderSectionHeader(
                    <MessageSquare size={16} className="text-secondary"/>,
                    <>Message App Integrations</>
                )}
                <RAGIntegrations
                    ragId={formData.id ?? ''}
                    integrations={getIntegrationsOfType('messaging')}
                    onUpdateIntegrations={handleUpdateMessagingIntegrations}
                />
            </div>

            {/* API Integration */}
            <div className="mb-3">
                {renderSectionHeader(
                    <Code size={16} className="text-secondary"/>,
                    <>API Integration</>
                )}
                <Card className="h-100">
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                                <h6 className="mb-1">REST API</h6>
                                <p className="text-muted small mb-0">Access your RAG through REST API endpoints</p>
                            </div>
                            <div className="d-flex align-items-center">
                                <div className="form-check form-switch me-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        checked={getIntegrationsOfType('api').length > 0}
                                        onChange={() => {
                                            const apiIntegrations = getIntegrationsOfType('api');
                                            if (apiIntegrations.length > 0) {
                                                updateIntegrations('api', []);
                                            } else {
                                                generateApiToken();
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        {getIntegrationsOfType('api').length > 0 && (
                            <>
                                {/* API Token Section */}
                                <div className="mb-4">
                                    <label className="form-label fw-medium small mb-2">API Token</label>
                                    <div className="d-flex gap-2">
                                        <div className="flex-grow-1 position-relative">
                                            <input
                                                type="text"
                                                className="form-control border"
                                                value={getApiToken()}
                                                readOnly
                                                style={{ height: '38px' }}
                                            />
                                        </div>
                                        <Button
                                            variant="outline-secondary"
                                            style={{ height: '38px' }}
                                            onClick={() => copyToClipboard(getApiToken(), setApiTokenCopied)}
                                        >
                                            {apiTokenCopied ? (
                                                <>
                                                    Copied!
                                                    <Copy size={16} className="ms-2" />
                                                </>
                                            ) : (
                                                <>
                                                    Copy
                                                    <Copy size={16} className="ms-2" />
                                                </>
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline-secondary"
                                            style={{ height: '38px' }}
                                            onClick={generateApiToken}
                                            title="Generate new token"
                                        >
                                            <RefreshCw size={16} />
                                        </Button>
                                    </div>
                                </div>

                                {/* Example Usage Section */}
                                <div>
                                    <label className="form-label fw-medium small mb-2">Example Usage</label>
                                    <div className="position-relative">
                                        <pre className="border rounded bg-light p-3" style={{ fontSize: '0.875rem' }}>
                                            {getCurlExample()}
                                        </pre>
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="position-absolute top-0 end-0 m-2"
                                            onClick={() => copyToClipboard(getCurlExample(), setCurlCopied)}
                                        >
                                            {curlCopied ? (
                                                <>
                                                    Copied!
                                                    <Copy size={14} className="ms-2" />
                                                </>
                                            ) : (
                                                <>
                                                    Copy
                                                    <Copy size={14} className="ms-2" />
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </div>
        </div>
    );

    return (
        <>
        <Card className="border-0 shadow-sm">
            <Card.Body className="p-4">
                {(error || submitError) && (
                    <Alert variant="danger" className="mb-3 py-2 px-3">
                        {error || submitError}
                    </Alert>
                )}

                <Form onSubmit={handleSubmit} className="mx-auto" style={{ maxWidth: '1100px' }}>
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k ?? 'basic')}
                        className="mb-4"
                    >
                        <Tab eventKey="basic" title="Basic Configuration">
                            {renderBasicConfigTab()}
                        </Tab>
                        <Tab eventKey="integrations" title="Integrations">
                            {renderIntegrationsTab()}
                        </Tab>
                    </Tabs>

                    <div className="d-flex justify-content-between align-items-center pt-3 mt-3">
                        {mode === 'edit' && onDelete && (
                            <Button
                                variant="danger"
                                onClick={() => setShowDeleteModal(true)}
                                disabled={isLoading}
                                style={{ height: '38px' }}
                            >
                                Delete
                            </Button>
                        )}

                        <div className={`d-flex gap-2 ${mode === 'edit' ? '' : 'ms-auto'}`}>
                            <Button
                                variant="light"
                                onClick={() => window.history.back()}
                                disabled={isLoading}
                                style={{ height: '38px' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isLoading || !formData.indexIds?.length}
                                style={{ height: '38px' }}
                            >
                                {isLoading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" role="status"/>
                                        {mode === 'edit' ? 'Saving...' : 'Creating...'}
                                    </>
                                ) : (
                                    mode === 'edit' ? 'Save' : 'Create Application'
                                )}
                            </Button>
                        </div>
                    </div>
                </Form>

                <KnowledgeBaseSelectionModal
                    isOpen={showKnowledgeBaseModal}
                    onClose={() => setShowKnowledgeBaseModal(false)}
                    onSelect={handleKnowledgeBaseSelection}
                    selectedKnowledgeBases={formData.indexIds}
                />
            </Card.Body>
        </Card>
            {mode === 'edit' && onDelete && (
                <DeleteConfirmationModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={() => onDelete?.()}
                    resourceType="RAG Application"
                    resourceName={formData.name}
                    isLoading={isLoading}
                />
            )}
        </>

    );
});


export default React.memo(RAGForm);